import {reactive} from 'vue';
import axios from 'axios';
import {translate} from '@elements/translations';

const DEFAULT_STEP_ORDER = ['contact', 'address', 'payment'];
let stepOrderResolved = false;

export const state = reactive({
    locale: 'de',
    stateUrl: '',
    accountStatusUrl: '',
    contactStepUrl: '',
    addressStepUrl: '',
    addressValidationUrl: '',
    paymentStepUrl: '',
    creditCardTokenUrl: '',
    voucherUrl: '',
    cartItemUrl: '',
    summaryUrl: '',
    confirmationUrl: '#',
    googleSsoUrl: '',
    forgotPasswordUrl: '',

    stepOrder: [...DEFAULT_STEP_ORDER],
    activeStep: 0,
    enabledSteps: new Set([0]),
    completedSteps: new Set(),
    summaries: ['—', '—', '—'],

    contactEmail: '',
    knownAccount: false,
    alreadySubscribed: false,
    newsletterOptIn: false,
    defaultCountry: 'AT',
    billingCountries: [],
    deliveryCountries: [],
    deliveryCountry: 'AT',
    addressPrefill: null,
    paymentPrefill: null,
    hasCreditCardToken: false,

    cart: {items: [], subtotal: 0, discounts: [], shippingLabel: '', shippingAmount: null, total: 0, currency: 'EUR', coupons: [], voucherTokenCodes: [], taxEntries: []},
    crossSell: [],
    paymentOptions: ['creditcard', 'applepay', 'paypal'],
    needsPayment: true,
    submitErrors: [],
});

export function initFromProps(props) {
    state.locale = props.locale || 'de';
    state.defaultCountry = props.defaultCountry || 'AT';
    state.billingCountries = Array.isArray(props.billingCountries) ? props.billingCountries : [];
    state.deliveryCountries = Array.isArray(props.deliveryCountries) ? props.deliveryCountries : [];
    state.stateUrl = props.stateUrl || '';
    state.accountStatusUrl = props.accountStatusUrl || '';
    state.contactStepUrl = props.contactStepUrl || '';
    state.addressStepUrl = props.addressStepUrl || '';
    state.addressValidationUrl = props.addressValidationUrl || '';
    state.paymentStepUrl = props.paymentStepUrl || '';
    state.creditCardTokenUrl = props.creditCardTokenUrl || '';
    state.voucherUrl = props.voucherUrl || '';
    state.cartItemUrl = props.cartItemUrl || '';
    state.summaryUrl = props.summaryUrl || '';
    state.confirmationUrl = props.confirmationUrl || '#';
    state.googleSsoUrl = props.googleSsoUrl || '';
    state.forgotPasswordUrl = props.forgotPasswordUrl || '';
    if (props.cart) {
        state.cart = props.cart;
    }
    state.crossSell = Array.isArray(props.crossSell) ? props.crossSell : [];
    if (Array.isArray(props.paymentOptions) && props.paymentOptions.length > 0) {
        state.paymentOptions = props.paymentOptions;
    }

    state.stepOrder = [...DEFAULT_STEP_ORDER];
    stepOrderResolved = false;
    state.activeStep = 0;
    state.enabledSteps = new Set([0]);
    state.completedSteps = new Set();
    state.summaries = ['—', '—', '—'];
    state.contactEmail = '';
    state.knownAccount = false;
    state.alreadySubscribed = false;
    state.newsletterOptIn = false;
    state.deliveryCountry = state.defaultCountry;
    state.addressPrefill = null;
    state.paymentPrefill = null;
    state.hasCreditCardToken = false;
}

export function goTo(index) {
    if (!state.enabledSteps.has(index)) {
        return;
    }
    state.activeStep = index;
}

function resetFrom(index) {
    for (let i = index + 1; i < state.stepOrder.length; i++) {
        state.completedSteps.delete(i);
        state.enabledSteps.delete(i);
        state.summaries[i] = '—';
    }
}

function advance(index, summary) {
    state.submitErrors = [];
    if (state.completedSteps.has(index)) {
        resetFrom(index);
    }
    state.completedSteps.add(index);
    state.summaries[index] = summary || '—';

    const next = index + 1;
    if (next < state.stepOrder.length) {
        state.enabledSteps.add(next);
    }
    if (state.activeStep === index && next < state.stepOrder.length) {
        state.activeStep = next;
    }
}

export function completeContact(payload) {
    state.knownAccount = payload.accountStatus !== 'guest';
    state.contactEmail = payload.email || '';
    state.newsletterOptIn = !!payload.newsletter;
    advance(state.stepOrder.indexOf('contact'), payload.summary);
}

export async function completeAddress(payload) {
    state.deliveryCountry = payload.country || state.defaultCountry;
    advance(state.stepOrder.indexOf('address'), payload.summary);
    await syncState();
}

// Alle Felder kommen bei jedem Aufruf frisch von der API - auch imageHtml, das serverseitig
// gerenderte Pimcore-<picture>-Markup (CheckoutApiService::buildThumbnailHtml()). Frueher wurde
// das Bild per productId aus dem Vorzustand weitergetragen, weil die API keines lieferte; ein
// per Cross-Sell hinzugefuegter Artikel hatte damit keinen Vorgaenger und blieb bis zum Reload
// ohne Bild. originalPrice ist null ohne aktiven Rabatt, variant ohne gepflegte Variantendaten.
function mapApiCart(apiCart) {
    const items = (apiCart.items || []).map((item) => ({
        id: item.productId,
        name: item.name,
        quantity: item.count,
        price: parseFloat(item.price),
        variant: item.variant ?? null,
        imageHtml: item.imageHtml ?? null,
        oldPrice: item.originalPrice != null ? parseFloat(item.originalPrice) : null,
        productVouchers: (item.productVouchers || []).map((voucher) => ({code: voucher.code, rule: voucher.rule})),
    }));

    const modifications = apiCart.priceModifications || [];
    const shippingMod = modifications.find((mod) => mod.key === 'shipping');
    const shippingAmount = shippingMod ? parseFloat(shippingMod.amount) : null;

    const discounts = modifications
        .filter((mod) => mod.key !== 'shipping' && parseFloat(mod.amount) < 0)
        .map((mod) => ({name: mod.name, amount: Math.abs(parseFloat(mod.amount)), voucherCode: mod.voucherCode || null}));

    const grandTotal = parseFloat(apiCart.grandTotal);
    const modsSum = modifications.reduce((sum, mod) => sum + parseFloat(mod.amount), 0);
    const subtotal = grandTotal - modsSum;

    let remainingTotal = grandTotal;
    const coupons = (apiCart.coupons || []).map((coupon) => {
        const redeemed = Math.min(parseFloat(coupon.amount) || 0, remainingTotal);
        remainingTotal -= redeemed;

        return {number: coupon.number, amount: redeemed};
    });

    // Codes, die schon als Rabattzeile oder am Artikel (productVouchers) angezeigt werden, nicht doppelt listen.
    const shownVoucherCodes = new Set([
        ...discounts.map((discount) => discount.voucherCode).filter(Boolean),
        ...items.flatMap((item) => item.productVouchers.map((voucher) => voucher.code)),
    ]);
    const voucherTokenCodes = (apiCart.voucherTokenCodes || []).filter((code) => !shownVoucherCodes.has(code));

    return {
        items,
        subtotal,
        discounts,
        shippingLabel: shippingAmount !== null ? formatPrice(shippingAmount, apiCart.currency) : '',
        shippingAmount,
        total: remainingTotal,
        currency: apiCart.currency || 'EUR',
        coupons,
        voucherTokenCodes,
        taxEntries: (apiCart.taxEntries || []).map((taxEntry) => ({percent: taxEntry.percent, amount: parseFloat(taxEntry.amount)})),
    };
}

function mapCrossSell(apiCrossSellingItems) {
    return (apiCrossSellingItems || []).map((item) => ({
        id: item.productId,
        name: item.name,
        price: parseFloat(item.price),
        imageHtml: item.imageHtml ?? null,
        // variantId ist die ID, die der Add-to-Cart-Endpunkt erwartet - das Label allein
        // laesst sich nicht zuverlaessig zurueckaufloesen.
        sizes: (item.sizes || []).map((size) => ({size: size.size, variantId: size.variantId})),
    }));
}

const priceFormatters = new Map();

export function formatPrice(amount, currency) {
    const resolvedCurrency = currency || 'EUR';
    // 'de'/'en' allein stellen das Symbol hinter den Betrag - '-AT' erzwingt die Reihenfolge,
    // die ueberall sonst im Checkout hartkodiert ist (z.B. "€ 239,90").
    const formatLocale = `${state.locale}-AT`;
    const cacheKey = `${formatLocale}|${resolvedCurrency}`;

    try {
        let formatter = priceFormatters.get(cacheKey);
        if (!formatter) {
            formatter = new Intl.NumberFormat(formatLocale, {style: 'currency', currency: resolvedCurrency});
            priceFormatters.set(cacheKey, formatter);
        }

        return formatter.format(amount);
    } catch (error) {
        return `€ ${amount.toFixed(2)}`;
    }
}

function mapApiPaymentOptions(apiPaymentOptions) {
    return (apiPaymentOptions || [])
        .filter((option) => option.allowed)
        .map((option) => option.code);
}

function resolveStepOrder(steps) {
    if (stepOrderResolved) {
        return;
    }

    // Flag bleibt bei ungueltigen Steps bewusst false, damit ein spaeterer syncState()-Call
    // (z.B. nach Abschluss des Address-Steps) noch eine valide stepOrder setzen kann.
    const backendOrder = steps.map((step) => step.key).filter((key) => DEFAULT_STEP_ORDER.includes(key));
    if (backendOrder.length === DEFAULT_STEP_ORDER.length && new Set(backendOrder).size === DEFAULT_STEP_ORDER.length) {
        state.stepOrder = backendOrder;
        stepOrderResolved = true;
    } else {
        console.error('Unexpected checkout steps from backend, keeping default step order', steps);
    }
}

// Laendername aus den bereits uebersetzten Laenderlisten, sonst der ISO-Code
function countryLabel(countryCode, countries) {
    if (!countryCode) {
        return '';
    }
    const country = (countries || []).find((option) => option.value === countryCode);

    return country ? country.label : countryCode;
}

function formatAddressLine(addr, countries) {
    return [
        `${addr.firstname || ''} ${addr.lastname || ''}`.trim(),
        [addr.street, addr.houseNumber].filter(Boolean).join(' '),
        [addr.zip, addr.city].filter(Boolean).join(' '),
        countryLabel(addr.countryCode, countries)
    ].filter(Boolean).join(', ');
}


export function buildAddressSummary(billing, delivery, billingCountries = [], deliveryCountries = []) {
    const billingLine = formatAddressLine(billing, billingCountries);
    const deliveryLine = delivery ? formatAddressLine(delivery, deliveryCountries) : '';

    if (!deliveryLine) {
        return billingLine || '—';
    }

    return [
        {key: 'billing', text: `${translate('checkout-one-page.modal.billing-label')}: ${billingLine}`},
        {key: 'delivery', text: `${translate('checkout-one-page.modal.delivery-label')}: ${deliveryLine}`},
    ];
}

export function summaryLines(summary) {
    return Array.isArray(summary) ? summary : [{key: 'single', text: summary}];
}

function applyStepsState(steps) {
    if (!Array.isArray(steps)) {
        return;
    }

    resolveStepOrder(steps);

    let highestCompleted = -1;
    const backendCompleted = new Set();
    for (const step of steps) {
        const index = state.stepOrder.indexOf(step.key);
        if (index === -1) {
            continue;
        }

        if (step.key === 'contact') {
            state.knownAccount = !!step.loggedIn;
            state.alreadySubscribed = !!step.newsletter;
            if (step.email) {
                state.contactEmail = step.email;
            }
            if (step.completed) {
                const statusLabel = translate(step.loggedIn ? 'checkout-one-page.contact.status-customer' : 'checkout-one-page.contact.status-guest');
                state.summaries[index] = step.email ? `${step.email} (${statusLabel})` : '—';
            }
        }
        if (step.key === 'address' && step.address) {
            state.addressPrefill = step.address;
            const country = step.address.sameAddress ? step.address.countryCode : step.address.deliveryCountryCode;
            if (country) {
                state.deliveryCountry = country;
            }
            if (step.completed) {
                const a = step.address;
                state.summaries[index] = buildAddressSummary(a, a.sameAddress ? null : {
                    firstname: a.deliveryFirstname,
                    lastname: a.deliveryLastname,
                    street: a.deliveryStreet,
                    houseNumber: a.deliveryHouseNumber,
                    zip: a.deliveryZip,
                    city: a.deliveryCity,
                    countryCode: a.deliveryCountryCode,
                }, state.billingCountries, state.deliveryCountries);
            }
        }
        if (step.key === 'payment') {
            state.paymentPrefill = step.selectedMethod || null;
            state.hasCreditCardToken = !!step.hasCreditCardToken;
            if (step.completed) {
                state.summaries[index] = step.selectedMethod
                    ? translate(`checkout-one-page.payment.${step.selectedMethod}`)
                    : '—';
            }
        }

        if (step.completed) {
            backendCompleted.add(index);
            if (index > highestCompleted) {
                highestCompleted = index;
            }
        }
    }

    for (let i = 0; i <= highestCompleted; i++) {
        if (backendCompleted.has(i)) {
            state.completedSteps.add(i);
        }
        if (i + 1 < state.stepOrder.length) {
            state.enabledSteps.add(i + 1);
        }
    }

    const firstOpen = state.stepOrder.findIndex((key, i) => !state.completedSteps.has(i));
    state.activeStep = firstOpen === -1 ? state.stepOrder.length - 1 : firstOpen;
}

export async function syncState() {
    if (!state.stateUrl) {
        return;
    }
    try {
        const response = await axios.get(state.stateUrl, {withCredentials: true});
        const data = response.data;
        if (data.cart) {
            state.cart = mapApiCart(data.cart);
            state.crossSell = mapCrossSell(data.cart.crossSellingItems);
        }
        if (Array.isArray(data.paymentOptions)) {
            state.paymentOptions = mapApiPaymentOptions(data.paymentOptions);
        }
        if (typeof data.needsPayment === 'boolean') {
            state.needsPayment = data.needsPayment;
        }
        if (Array.isArray(data.errors)) {
            state.submitErrors = data.errors;
            pushCheckoutErrors(data.errors.map((key) => translate('checkout.error.' + key)));
        }
        applyStepsState(data.steps);
    } catch (error) {
        console.error('Could not sync checkout state', error);
    }
}

// Liest nur cart/crossSell/paymentOptions/needsPayment und passt daher auf jede Antwort,
// die den Cart-State mitschickt - Voucher wie Add-to-Cart.
function applyCartResponse(data) {
    state.submitErrors = [];
    state.cart = mapApiCart(data.cart);
    state.crossSell = mapCrossSell(data.cart.crossSellingItems);
    if (Array.isArray(data.paymentOptions)) {
        state.paymentOptions = mapApiPaymentOptions(data.paymentOptions);
    }
    if (typeof data.needsPayment === 'boolean') {
        state.needsPayment = data.needsPayment;
    }
}

export async function applyVoucher(code) {
    if (!state.voucherUrl) {
        return {success: false, errorKey: null};
    }
    try {
        const response = await axios.post(state.voucherUrl, {code}, {withCredentials: true});
        applyCartResponse(response.data);

        return {success: !response.data.errorKey, errorKey: response.data.errorKey || null};
    } catch (error) {
        console.error('Could not apply voucher code', error);

        return {success: false, errorKey: null};
    }
}

export async function removeVoucher(code) {
    if (!state.voucherUrl) {
        return false;
    }
    try {
        const response = await axios.delete(`${state.voucherUrl}/${encodeURIComponent(code)}`, {withCredentials: true});
        applyCartResponse(response.data);

        return true;
    } catch (error) {
        console.error('Could not remove voucher code', error);

        return false;
    }
}

// Die JSON-API liefert die dataLayer-Payloads, die der klassische Checkout beim Seitenaufruf
// bzw. Formular-Submit pusht (OnePageCheckoutTracking), bereits als Objekte.
export function pushDataLayer(payloads) {
    if (!Array.isArray(window.dataLayer)) {
        return;
    }
    for (const payload of payloads || []) {
        if (payload) {
            window.dataLayer.push(payload);
        }
    }
}

// GA4-Fehlertracking: jede Meldung, die dem User angezeigt wird, mit ihrem angezeigten Text.
export function pushCheckoutErrors(messages) {
    pushDataLayer((messages || []).filter(Boolean).map((message) => ({event: 'checkout_error', error_message: message})));
}

export function pushVoucherError(code, message) {
    pushDataLayer([{event: 'voucher_error', error_voucher: code, error_message: message}]);
}

// Der klassische Flow pusht dieselben Payloads in cart.js, nur muss er sie vorher parsen.
function pushTracking(data) {
    pushDataLayer([data.tracking, data.trackingGa4]);
}

export async function addCartItem(variantId) {
    if (!state.cartItemUrl) {
        return {success: false, errorKey: null};
    }
    try {
        const response = await axios.post(state.cartItemUrl, {variantId, amount: 1}, {withCredentials: true});
        applyCartResponse(response.data);
        pushTracking(response.data);

        return {success: !response.data.errorKey, errorKey: response.data.errorKey || null};
    } catch (error) {
        console.error('Could not add item to cart', error);

        return {success: false, errorKey: null};
    }
}
